import { makeZip } from 'client-zip';
import { convertNanosecondTimestampToDate } from '@lucidlink/workspacemanagement';
import { streamFileChunksConcurrently, streamFileChunksSequentially, } from '../../../file-contexts-common';
import { CONCURRENT_DOWNLOAD_FILE_SIZE_THRESHOLD, DEFAULT_FILE_DOWNLOAD_CHUNK_SIZE, ZERO_BYTES_BIG_INT, } from '../../constants';
const withoutAbruptCompletion = (iterable) => {
    const iterator = iterable[Symbol.asyncIterator]();
    return {
        [Symbol.asyncIterator]: () => ({ next: async () => iterator.next() }),
    };
};
async function* withArchivedReport(chunks, file, onFileArchived) {
    yield* chunks;
    onFileArchived(file);
}
const probeFirstChunk = async (downloadChunk) => {
    const firstChunkBuffer = new Uint8Array(DEFAULT_FILE_DOWNLOAD_CHUNK_SIZE);
    const firstChunk = await downloadChunk(firstChunkBuffer, ZERO_BYTES_BIG_INT);
    return (buffer, offset) => {
        if (offset === ZERO_BYTES_BIG_INT) {
            buffer.set(firstChunkBuffer.subarray(0, firstChunk));
            return Promise.resolve(firstChunk);
        }
        return downloadChunk(buffer, offset);
    };
};
async function* createArchiveEntrySpecs({ createChunkDownloader, downloadableEntries, onFileArchived, skipUnreadableExternalFile, }) {
    for await (const entry of downloadableEntries) {
        if (entry.kind === 'folder') {
            yield { name: entry.path };
            continue;
        }
        const { file } = entry;
        let downloadChunk = createChunkDownloader(file);
        if (file.isExternalFile) {
            try {
                downloadChunk = await probeFirstChunk(downloadChunk);
            }
            catch (error) {
                if (skipUnreadableExternalFile &&
                    skipUnreadableExternalFile(error)) {
                    continue;
                }
                throw error;
            }
        }
        const willDownloadConcurrently = file.fsEntrySize > CONCURRENT_DOWNLOAD_FILE_SIZE_THRESHOLD;
        const input = willDownloadConcurrently
            ? streamFileChunksConcurrently({
                chunkSize: DEFAULT_FILE_DOWNLOAD_CHUNK_SIZE,
                downloadChunk,
                fileSize: file.fsEntrySize,
            })
            : streamFileChunksSequentially({
                chunkSize: DEFAULT_FILE_DOWNLOAD_CHUNK_SIZE,
                downloadChunk,
            });
        yield {
            name: file.zipPath,
            size: file.fsEntrySize,
            lastModified: convertNanosecondTimestampToDate(file.updateTime),
            input: onFileArchived
                ? withArchivedReport(input, file, onFileArchived)
                : input,
        };
    }
}
export const createArchiveStream = (args) => makeZip(withoutAbruptCompletion(createArchiveEntrySpecs(args)));
